import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Picture from '../components/Picture';
import {
    Rocket,
    Target,
    Award,
    TrendingUp,
    ShieldCheck,
    Zap,
    Users,
    Globe,
    CheckCircle,
    Map,
    Compass,
    ArrowRight
} from 'lucide-react';
import StudentPromise from '../components/StudentPromise';
import FeaturedVideo from '../components/FeaturedVideo';
import Footer from '../components/Footer';


export default function Home() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [mounted, setMounted] = useState(false);
    const isPhase1 = true; // Phase 1 (June 2026) launch — Design vertical only. Flip to false when expanding to all streams.

    useEffect(() => {
        setMounted(true);
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    return (
        <div className="h-screen overflow-y-auto lg:snap-y lg:snap-mandatory scroll-pt-16 bg-slate-950 selection:bg-indigo-500/30">

            {/* ══════════════════════════════════════════════════════════
              PRISM BETA — Hidden on production until the experience is
              ready for general audiences. Set the constant to `true`
              (or restore the env-flag check) to re-enable.
              ══════════════════════════════════════════════════════════ */}
            {false && (
                <Link
                    to="/prism"
                    data-testid="prism-home-banner"
                    className="block fixed top-16 inset-x-0 z-30 bg-slate-950/80 backdrop-blur-md border-b border-amber-400/15 hover:bg-slate-900/90 transition-colors group"
                >
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-center gap-3 text-center">
                        <span className="text-[9px] sm:text-[10px] font-black text-amber-300 uppercase tracking-[0.25em] flex-shrink-0">Eager Prism · Beta</span>
                        <span className="hidden sm:inline w-1 h-1 rounded-full bg-amber-400/40" />
                        <span className="text-[11px] sm:text-xs text-slate-300 font-medium">
                            Try the 2-minute reflection
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-black text-amber-300 group-hover:translate-x-0.5 transition-transform">
                            Begin →
                        </span>
                    </div>
                </Link>
            )}

            {/* ══════════════════════════════════════════════════════════
              HERO SECTION — Two Column (Text Left | Image Right)
              ══════════════════════════════════════════════════════════ */}
            <div className="lg:snap-start min-h-screen relative overflow-hidden bg-slate-950 flex items-center">

                {/* Background glows & grid */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className={`absolute bottom-1/4 -left-20 w-[600px] h-[600px] bg-rose-500/15 rounded-full blur-[120px] transition-all duration-1000 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}></div>
                    <div className={`absolute top-0 -right-20 w-[700px] h-[700px] bg-indigo-600/20 rounded-full blur-[130px] transition-all duration-1000 delay-300 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}></div>
                    <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: "url('/noise.svg')" }}></div>
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]"></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-10 pt-24 pb-12 lg:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

                        {/* LEFT — Text content */}
                        <div className="flex flex-col items-start text-left">
                            {/* BETA Badge */}
                            <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900/60 border border-white/10 text-indigo-300 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-8 backdrop-blur-xl transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.8)]"></span>
                                {isPhase1 ? t('home.june.badge') : t('home.badge')}
                            </div>

                            {/* Headline */}
                            <h1 className={`text-[2.25rem] leading-[1.08] md:text-5xl lg:text-6xl xl:text-7xl md:leading-[1.05] font-black text-white tracking-tight mb-6 transition-all duration-1000 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                                <Trans
                                    i18nKey={isPhase1 ? "home.june.hero_title" : "home.hero_title"}
                                    components={{
                                        0: <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-violet-400 to-indigo-400" />
                                    }}
                                />
                            </h1>

                            {/* Subtitle */}
                            <p className={`text-base md:text-lg text-slate-400 mb-8 sm:mb-10 leading-relaxed font-medium max-w-lg transition-all duration-1000 delay-300 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                                <Trans
                                    i18nKey={isPhase1 ? "home.june.hero_subtitle" : "home.hero_subtitle"}
                                    components={{ 0: <span className="text-white font-bold" /> }}
                                />
                            </p>

                            {/* CTAs */}
                            <div className={`w-full sm:w-auto flex flex-col sm:flex-row items-stretch sm:items-start gap-3 sm:gap-4 transition-all duration-1000 delay-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                                <button
                                    onClick={() => navigate('/quiz')}
                                    className="px-6 sm:px-8 py-3.5 sm:py-4 bg-[#6366f1] text-white rounded-2xl font-black text-sm sm:text-base transition-all hover:bg-[#4f46e5] hover:scale-105 active:scale-95 flex items-center justify-center sm:justify-start gap-2.5 sm:gap-3 group shadow-[0_0_30px_rgba(99,102,241,0.4)]"
                                >
                                    <Rocket className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                    {t('home.cta_quiz')}
                                </button>
                                <button
                                    onClick={() => navigate('/explore')}
                                    data-testid="home-browse-courses"
                                    className="group px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-br from-fuchsia-500 via-indigo-600 to-indigo-700 hover:from-fuchsia-400 hover:via-indigo-500 hover:to-indigo-600 text-white rounded-2xl font-black text-sm sm:text-base transition-all flex items-center justify-center sm:justify-start gap-2 hover:scale-105 active:scale-95 shadow-[0_0_30px_rgba(99,102,241,0.4)] ring-1 ring-indigo-400/40"
                                >
                                    <Compass className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                                    {t('home.cta_explore')}
                                </button>
                            </div>

                            {/* Stats — Phase 1 honest numbers */}
                            <div className={`mt-12 sm:mt-16 grid grid-cols-3 gap-4 sm:gap-6 pt-7 sm:pt-8 border-t border-white/5 transition-all duration-1000 delay-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                                <div className="flex flex-col">
                                    <span className="text-2xl sm:text-3xl font-black text-white tracking-tighter">30</span>
                                    <span className="text-[10px] sm:text-[10px] font-black uppercase tracking-[0.12em] sm:tracking-[0.15em] text-slate-500 mt-1 leading-tight">Career Archetypes</span>
                                </div>
                                <div className="flex flex-col border-x border-white/5 px-3 sm:px-4">
                                    <span className="text-2xl sm:text-3xl font-black text-white tracking-tighter">02</span>
                                    <span className="text-[10px] sm:text-[10px] font-black uppercase tracking-[0.12em] sm:tracking-[0.15em] text-slate-500 mt-1 leading-tight">Partnered Colleges</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-2xl sm:text-3xl font-black text-white tracking-tighter">100%</span>
                                    <span className="text-[10px] sm:text-[10px] font-black uppercase tracking-[0.12em] sm:tracking-[0.15em] text-slate-500 mt-1 leading-tight">Free Career Quiz</span>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT — Hero Image (visible on all screen sizes; mobile-first) */}
                        <div className={`flex justify-center lg:justify-end items-center order-first lg:order-last transition-all duration-1000 delay-200 ${mounted ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
                            <div className="w-full max-w-[360px] lg:max-w-[520px] aspect-[4/3] rounded-[24px] lg:rounded-[32px] overflow-hidden shadow-2xl shadow-black/60 border border-white/10 relative">
                                <Picture
                                    src="/images/home_hero.png"
                                    alt="Creative student working in studio"
                                    className="w-full h-full object-cover"
                                    priority
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-slate-950/30 to-transparent pointer-events-none"></div>
                            </div>
                        </div>

                    </div>

                </div>
            </div>


            {/* ══════════════════════════════════════════════════════════
            HOW IT WORKS — 4 Steps
            ══════════════════════════════════════════════════════════ */}
            <div className="lg:snap-start min-h-screen flex items-center bg-slate-50 relative overflow-hidden py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-950 mb-8 tracking-tight leading-tight">
                                Your journey to a <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1a0c2e] via-[#701a75] to-[#1a0c2e]">Verified Career</span> in 4 steps.
                            </h2>

                            <div className="space-y-4">
                                <div onClick={() => navigate('/quiz')} className="flex gap-6 p-4 rounded-3xl hover:bg-white hover:shadow-[0_20px_60px_-15px_rgba(112,26,117,0.15)] border border-transparent hover:border-fuchsia-100 transition-all duration-500 cursor-pointer group">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#1a0c2e] via-[#701a75] to-[#1a0c2e] rounded-xl flex items-center justify-center font-black text-white shadow-xl shadow-fuchsia-900/20 group-hover:scale-125 group-hover:rotate-3 group-hover:shadow-[0_20px_40px_-10px_rgba(112,26,117,0.5)] transition-all duration-500">1</div>
                                    <div className="flex-grow">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-xl font-black text-slate-950 group-hover:text-fuchsia-700 transition-colors border-b-2 border-transparent group-hover:border-fuchsia-200">Build Your Identity</h4>
                                            <ArrowRight className="w-5 h-5 text-fuchsia-600 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                                        </div>
                                        <p className="text-slate-500 font-medium text-sm mt-1 leading-relaxed max-w-lg">Complete a quick 5-minute quiz to understand your logic, creativity, and leadership skills.</p>
                                    </div>
                                </div>

                                <div onClick={() => navigate('/library')} className="flex gap-6 p-4 rounded-3xl hover:bg-white hover:shadow-[0_20px_60px_-15px_rgba(112,26,117,0.15)] border border-transparent hover:border-fuchsia-100 transition-all duration-500 cursor-pointer group">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#1a0c2e] via-[#701a75] to-[#1a0c2e] rounded-xl flex items-center justify-center font-black text-white shadow-xl shadow-fuchsia-900/20 group-hover:scale-125 group-hover:rotate-3 group-hover:shadow-[0_20px_40px_-10px_rgba(112,26,117,0.5)] transition-all duration-500">2</div>
                                    <div className="flex-grow">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-xl font-black text-slate-950 group-hover:text-fuchsia-700 transition-colors border-b-2 border-transparent group-hover:border-fuchsia-200">Explore Opportunities</h4>
                                            <ArrowRight className="w-5 h-5 text-fuchsia-600 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                                        </div>
                                        <p className="text-slate-500 font-medium text-sm mt-1 leading-relaxed max-w-lg">Dive into our career library to discover roles and industry trends tailored to you.</p>
                                    </div>
                                </div>

                                <div onClick={() => navigate('/colleges')} className="flex gap-6 p-4 rounded-3xl hover:bg-white hover:shadow-[0_20px_60px_-15px_rgba(112,26,117,0.15)] border border-transparent hover:border-fuchsia-100 transition-all duration-500 cursor-pointer group">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#1a0c2e] via-[#701a75] to-[#1a0c2e] rounded-xl flex items-center justify-center font-black text-white shadow-xl shadow-fuchsia-900/20 group-hover:scale-125 group-hover:rotate-3 group-hover:shadow-[0_20px_40px_-10px_rgba(112,26,117,0.5)] transition-all duration-500">3</div>
                                    <div className="flex-grow">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-xl font-black text-slate-950 group-hover:text-fuchsia-700 transition-colors border-b-2 border-transparent group-hover:border-fuchsia-200">Discover Best-Fit Paths</h4>
                                            <ArrowRight className="w-5 h-5 text-fuchsia-600 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                                        </div>
                                        <p className="text-slate-500 font-medium text-sm mt-1 leading-relaxed max-w-lg">Browse verified colleges and entrance exams based on your unique strengths.</p>
                                    </div>
                                </div>

                                <div onClick={() => navigate('/benefits')} className="flex gap-6 p-4 rounded-3xl hover:bg-white hover:shadow-[0_20px_60px_-15px_rgba(112,26,117,0.15)] border border-transparent hover:border-fuchsia-100 transition-all duration-500 cursor-pointer group">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#1a0c2e] via-[#701a75] to-[#1a0c2e] rounded-xl flex items-center justify-center font-black text-white shadow-xl shadow-fuchsia-900/20 group-hover:scale-125 group-hover:rotate-3 group-hover:shadow-[0_20px_40px_-10px_rgba(112,26,117,0.5)] transition-all duration-500">4</div>
                                    <div className="flex-grow">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-xl font-black text-slate-950 group-hover:text-fuchsia-700 transition-colors border-b-2 border-transparent group-hover:border-fuchsia-200">Claim Your Benefits</h4>
                                            <ArrowRight className="w-5 h-5 text-fuchsia-600 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                                        </div>
                                        <p className="text-slate-500 font-medium text-sm mt-1 leading-relaxed max-w-lg">Secure admission with Eager support and unlock your Student Grant instantly and drop out insurance. Get on time industry updates.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-5 relative flex justify-center lg:justify-end">
                            <div className="w-full max-w-[340px] bg-gradient-to-tr from-indigo-100 to-white rounded-[40px] border border-white shadow-2xl overflow-hidden group">
                                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:32px_32px]"></div>
                                <div className="relative p-10 text-center flex flex-col items-center">
                                    <div className="w-20 h-20 bg-white rounded-3xl shadow-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                                        <CheckCircle className="w-10 h-10 text-indigo-600" />
                                    </div>
                                    <p className="text-2xl font-black text-slate-950 tracking-tight mb-3">Your Verified Roadmap</p>
                                    <p className="text-slate-500 font-medium text-sm mb-8 leading-relaxed">Complete the 4 steps to build your identity and unlock your Eager Verified status.</p>
                                    <button onClick={() => navigate('/quiz')} className="w-full py-4 bg-slate-950 text-white rounded-2xl font-black text-sm hover:bg-indigo-600 transition-all duration-300 shadow-lg">
                                        Start Step 1
                                    </button>
                                </div>
                            </div>
                            <div className="absolute -top-6 -right-6 w-24 h-24 bg-indigo-600/10 rounded-full blur-3xl"></div>
                            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-blue-600/10 rounded-full blur-3xl"></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════
            STUDENT PROMISE
            ══════════════════════════════════════════════════════════ */}
            <div className="lg:snap-start min-h-screen flex items-center bg-slate-50 relative overflow-hidden">
                <div className="w-full">
                    <StudentPromise />
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════
            FEATURED VIDEO — "Eager Overview" (admin-configurable).
            Falls back to the launch overview video so this section never
            renders blank — admin can override the URL anytime via
            /admin → Featured Videos.
            ══════════════════════════════════════════════════════════ */}
            <div className="lg:snap-start bg-slate-950 relative overflow-hidden py-24 sm:py-32">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] bg-indigo-600/10 rounded-full blur-[120px]"></div>
                    <div className="absolute bottom-1/4 -right-32 w-[480px] h-[480px] bg-amber-500/5 rounded-full blur-[140px]"></div>
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:40px_40px]"></div>
                </div>
                <div className="relative w-full">
                    <FeaturedVideo
                        slot="home_overview"
                        tone="dark"
                        fallbackUrl="https://youtu.be/dZEJ_LzVlW8"
                        fallbackTitle="Watch our 2-minute overview"
                        fallbackSubtitle="Built in India, for India's next generation."
                    />
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════
            FOOTER
            ══════════════════════════════════════════════════════════ */}
            <div className="lg:snap-start pt-16">
                <Footer />
            </div>

        </div>
    );
}
